// 统一的 Supabase API 客户端
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { AppData } from '../types';

// 兼容 Node 环境：process 在构建时存在，但 shared 包不依赖 @types/node
declare const process: { env?: Record<string, string | undefined> } | undefined;

// 从环境变量获取 Supabase 配置。必须直接使用 import.meta.env.VITE_* 才能被 Vite 在构建时替换
const supabaseUrl =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL) ||
  (typeof process !== 'undefined' && process?.env?.VITE_SUPABASE_URL) ||
  '';

const supabaseAnonKey =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_ANON_KEY) ||
  (typeof process !== 'undefined' && process?.env?.VITE_SUPABASE_ANON_KEY) ||
  '';

// 创建自定义 fetch 函数，添加超时和错误处理
function createFetchWithTimeout(timeoutMs: number = 60000) {
  return async (url: RequestInfo | URL, options: RequestInit = {}) => {
    const existingSignal = options.signal;
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    timeoutId = setTimeout(() => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    }, timeoutMs);

    if (existingSignal) {
      existingSignal.addEventListener('abort', () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        controller.abort();
      });
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      return response;
    } catch (error: any) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (existingSignal?.aborted) {
        throw error;
      }
      
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        const urlStr = typeof url === 'string' ? url : url.toString();
        console.warn('[Supabase] 请求超时:', urlStr);
        const timeoutError = new Error('网络请求超时，请检查网络连接后重试');
        (timeoutError as any).name = 'TimeoutError';
        (timeoutError as any).isTimeout = true;
        throw timeoutError;
      }
      
      if (error.message?.includes('Failed to fetch') || 
          error.message?.includes('ERR_TIMED_OUT') ||
          error.message?.includes('NetworkError')) {
        const urlStr = typeof url === 'string' ? url : url.toString();
        console.warn('[Supabase] 网络连接失败:', urlStr);
        const networkError = new Error('网络连接失败，请检查网络设置');
        (networkError as any).name = 'NetworkError';
        (networkError as any).isNetworkError = true;
        throw networkError;
      }
      
      throw error;
    }
  };
}

// 浏览器环境下打一条诊断日志（不输出密钥），便于排查「Supabase 未配置」（用 log 避免被控制台「Verbose」过滤）
if (typeof window !== 'undefined') {
  const configured = Boolean(supabaseUrl && supabaseAnonKey);
  console.log(
    '[Supabase]',
    configured ? '已配置' : '未配置（构建时未注入 VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY，请检查 Cloudflare 环境变量并重新部署）'
  );
}

// 创建 Supabase 客户端
export const supabase: SupabaseClient | null = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      },
      global: {
        fetch: createFetchWithTimeout(30000),
      },
    })
  : null;

// 检查 Supabase 是否可用
export function isSupabaseAvailable(): boolean {
  return supabaseUrl !== '' && supabaseAnonKey !== '';
}

// 检查 Supabase 是否已初始化
export function isSupabaseInitialized(): boolean {
  return supabase !== null;
}
